<template>
  <div class="cg-set">
    <div class="cg-header">
      <span
        class="cg-dot"
        :class="groupby.isActive ? 'cg-dot--on' : 'cg-dot--off'"
        @click="groupby.isActive = !groupby.isActive"
      />
      <span
        class="cg-add"
        @click="addGroup"
      >
        <feather-icon
          icon="PlusIcon"
          size="10"
        /> группировка
      </span>
    </div>
    <div
      v-for="(grp, gi) in groupby.groups"
      :key="gi"
      class="cg-line"
    >
      <span
        class="cg-dot"
        :class="grp.isActive ? 'cg-dot--on' : 'cg-dot--off'"
        @click="grp.isActive = !grp.isActive"
      />
      <!-- oper -->
      <span
        class="cg-word cg-word--oper"
        @click="open(gi, 'oper')"
      >{{ operLabel(grp) }}</span>
      <!-- entity -->
      <span
        class="cg-word"
        :class="{ 'cg-word--empty': !hasEntity(grp) }"
        @click="open(gi, 'entity')"
      >{{ entityLabel(grp) }}</span>
      <!-- result entity (for countdistinct) -->
      <template v-if="grp.oper && grp.oper.sid === 'countdistinct'">
        <span class="cg-arrow">→</span>
        <span
          class="cg-word"
          :class="{ 'cg-word--empty': !hasResEntity(grp) }"
          @click="open(gi, 'result')"
        >{{ resEntityLabel(grp) }}</span>
      </template>
      <span
        class="cg-del"
        @click="removeGroup(gi)"
      >
        <feather-icon
          icon="XIcon"
          size="10"
        />
      </span>
      <!-- dropdown panel -->
      <div
        v-if="activeRow === gi && activeKind"
        class="cg-panel"
      >
        <b-form-input
          :ref="'pi_' + gi + '_' + activeKind"
          v-model="search"
          size="sm"
          placeholder="Поиск..."
          autocomplete="off"
          @input="onSearch"
          @keydown.esc="close"
          @keydown.enter.prevent="pickFirst(gi)"
        />
        <div
          v-if="filtered.length"
          class="cg-list"
        >
          <div
            v-for="item in filtered"
            :key="item.value"
            class="cg-opt"
            @click.stop="pick(gi, item)"
          >{{ item.text }}</div>
        </div>
        <div
          v-else-if="search"
          class="cg-empty"
        >
          Не найдено
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'

let cachedOpers = null
let opersLoading = false
let opersCallbacks = []

export default {
  name: 'CbsGroupbyConstructor',
  components: { BFormInput },
  props: {
    groupby: { type: Object, required: true },
    fields: { type: Array, default: () => [] },
    resultFields: { type: Array, default: () => [] },
  },
  data() {
    return {
      opers: cachedOpers || [
        { sid: 'group', name: 'Group' },
        { sid: 'sum', name: 'Sum' },
      ],
      activeRow: -1,
      activeKind: null,
      search: '',
      source: [],
      filtered: [],
    }
  },
  created() {
    if (!cachedOpers) this.loadOpers()
  },
  mounted() {
    this._outsideClick = e => {
      if (this.activeKind && !e.target.closest('.cg-line')) this.close()
    }
    document.addEventListener('click', this._outsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this._outsideClick)
  },
  methods: {
    // --- labels ---
    operLabel(g) { return (g.oper && (g.oper.name || g.oper.sid)) || '(операнд)' },
    entityLabel(g) { return (g.entity && (g.entity.title || g.entity.entitysid)) || '(поле)' },
    resEntityLabel(g) { return (g.resentity && (g.resentity.title || g.resentity.entitysid)) || '(результат)' },
    hasEntity(g) { return g.entity && (g.entity.title || g.entity.entitysid || g.entity.value) },
    hasResEntity(g) { return g.resentity && (g.resentity.title || g.resentity.entitysid || g.resentity.value) },

    // --- picker ---
    open(gi, kind) {
      if (this.activeRow === gi && this.activeKind === kind) { this.close(); return }
      this.search = ''
      this.source = this.buildSource(gi, kind)
      this.filtered = this.source
      this.activeRow = gi
      this.activeKind = kind
      this.$nextTick(() => {
        const ref = this.$refs[`pi_${gi}_${kind}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el) el.focus()
      })
    },
    close() { this.activeRow = -1; this.activeKind = null; this.search = ''; this.source = []; this.filtered = [] },
    onSearch() {
      if (!this.search.trim()) { this.filtered = this.source; return }
      const parts = this.search.trim().toLowerCase().split(/\s+/)
      const re = new RegExp(parts.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*'))
      this.filtered = this.source.filter(i => re.test((i.text || '').toLowerCase()))
    },
    buildSource(gi, kind) {
      if (kind === 'oper') return this.opers.map(o => ({ value: o.sid, text: o.name, _o: o }))
      if (kind === 'entity') return this.fields.map(f => ({ value: f.value, text: f.title || f.entitysid || String(f.value), _f: f }))
      if (kind === 'result') return this.resultFields.map(f => ({ value: f.value, text: f.title || f.entitysid || String(f.value), _f: f }))
      return []
    },
    pickFirst(gi) { if (this.filtered.length) this.pick(gi, this.filtered[0]) },
    pick(gi, item) {
      const grp = this.groupby.groups[gi]
      const kind = this.activeKind
      if (kind === 'oper') {
        this.$set(grp, 'oper', item._o ? { sid: item._o.sid, name: item._o.name } : { sid: item.value, name: item.text })
      } else if (kind === 'entity') {
        this.$set(grp, 'entity', item._f || null)
      } else if (kind === 'result') {
        this.$set(grp, 'resentity', item._f || null)
      }
      this.close()
    },

    // --- add/remove ---
    addGroup() {
      if (!this.groupby.groups) this.$set(this.groupby, 'groups', [])
      this.groupby.groups.push({
        isActive: true,
        name: 'group',
        oper: { sid: 'group', name: 'Group' },
        entity: null,
      })
      this.groupby.isActive = true
    },
    removeGroup(i) { this.groupby.groups.splice(i, 1); if (this.activeRow === i) this.close() },

    // --- load opers ---
    loadOpers() {
      if (opersLoading) { opersCallbacks.push(o => { this.opers = o }); return }
      opersLoading = true
      useJwt.query({ query: { method: 'groupbyoperands', param: {} } })
        .then(r => { if (r.data.thread) this.pollOpers(r.data.thread) })
        .catch(() => { opersLoading = false })
    },
    pollOpers(thread) {
      setTimeout(() => {
        useJwt.query({ query: { method: 'thread', param: { threadname: thread } } })
          .then(r => {
            if (r.data.thread && r.data.thread.status === 'done') {
              const { opers } = r.data.thread.result
              cachedOpers = opers; this.opers = opers; opersLoading = false
              opersCallbacks.forEach(cb => cb(opers)); opersCallbacks = []
            } else { this.pollOpers(thread) }
          })
          .catch(() => { opersLoading = false })
      }, 500)
    },
  },
}
</script>

<style lang="scss" scoped>
.cg-set {
  font-size: 12px;
}

.cg-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  user-select: none;
}

.cg-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
}
.cg-dot--on { background: #28c76f; }
.cg-dot--off { background: #ea5455; }

.cg-add {
  font-size: 11px;
  color: #b9b9c3;
  cursor: pointer;
  padding: 1px 4px;
  border-radius: 3px;
  white-space: nowrap;
  &:hover { color: #28c76f; background: rgba(40, 199, 111, 0.1); }
}

.cg-line {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0 3px 12px;
  line-height: 1.4;
  flex-wrap: wrap;
}

.cg-word {
  color: #7367f0;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  border-bottom: 1px dashed rgba(115, 103, 240, 0.4);
  &:hover { border-bottom-color: #7367f0; }
}

.cg-word--empty {
  color: #b9b9c3;
  border-bottom-color: rgba(185, 185, 195, 0.4);
  font-style: italic;
  &:hover { color: #7367f0; border-bottom-color: #7367f0; }
}

.cg-word--oper {
  font-weight: 600;
  color: #ff9f43;
  border-bottom-color: rgba(255, 159, 67, 0.4);
  &:hover { border-bottom-color: #ff9f43; }
}

.cg-arrow {
  color: #b9b9c3;
}

.cg-del {
  color: #b9b9c3;
  cursor: pointer;
  padding: 1px 3px;
  border-radius: 3px;
  line-height: 1;
  flex-shrink: 0;
  &:hover { color: #ea5455; background: rgba(234, 84, 85, 0.1); }
}

.cg-panel {
  position: absolute;
  top: 100%;
  left: 12px;
  z-index: 10;
  width: 220px;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
}

.cg-list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.cg-opt {
  padding: 3px 8px;
  cursor: pointer;
  border-radius: 3px;
  color: #5e5873;
  &:hover { background: #f8f8f8; }
}

.cg-empty {
  padding: 6px 8px;
  color: #b9b9c3;
}

// Dark mode
.dark-layout {
  .cg-add { color: #4b4b5a; }
  .cg-del { color: #4b4b5a; }
  .cg-word { color: #7367f0; }
  .cg-word--empty { color: #4b4b5a; }
  .cg-word--oper { color: #ff9f43; }
  .cg-panel {
    background: #283046;
    border-color: #3b4253;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
  .cg-opt {
    color: #d0d2d6;
    &:hover { background: #3b4253; }
  }
}
</style>
